<template>
    <div>
        <img class="hero-image" src="@/assets/images/banners/restart-banner.jpg" height="301" width="100%" alt="Selected indicator icon"/>
        <div class="main-page">
            <div class="full-page">
                <h2 class="main-hero" >{{ $t('DASHBOARD_SEE_YOU_AGAIN', { firstName }) }}</h2>
                <p class="sub-text" v-text="$t('RESTART_PAGE_IF_YOU_D_LIKE')"></p>
                <p v-html="$t('RESTART_PAGE_SELECT_BOX')"></p>
                <div class="selectBox">
                    <select-box
                        v-model="selectedProduct"
                        :options="availableProducts"/>
                        </div>
                <p class="restart--select-frequency" v-html="$t('RESTART_PAGE_SELECT_FREQUENCY')"></p>
                <radio-field
                    v-for="(item, index) in planOptions"
                    :key="index"
                    v-model="selectedPlan"
                    :option="item.plan_slug"
                    :label="item.plan_name"/>
                <div class="mt-5">
                    <restart-account-coupon-field
                            class="discount-text"
                            :is-valid="!errorHandler.has('coupon_code')"
                            :message="errorHandler.get('coupon_code')"
                            v-model="couponCode"/>

                    <div class="pt-5">
                        <button class="btn--primary restart-button" v-if="outstandingOrders.length"
                            @click="go('account.outstanding-orders')">
                            {{$t('DASHBOARD_OUTSTANDING_ORDERS_BUTTON')}}
                        </button>
                    </div>

                    <div class="pt-4">
                        <button
                            class="btn--primary restart-button"
                            :class="outstandingOrders.length && 'button--greyed-out'"
                            @click="restartSubscription(inactiveId[0], couponCode, selectedPlan)"
                            :disabled="buttonDisabled || outstandingOrders.length > 0"
                            v-text="$t('RESTART_PAGE_RESTART_DELIVERIES')">
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <hr class="tag-style">
        <multi-teaser :fields="restartFields"/>
        <div>
            <choose-gift-type/>
        </div>
        <div class="background--paper p-1 divider--container">
            <div class="divider-white--top"></div>
        </div>
        <floating-banner/>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { MultiTeaser } from '@freddiesflowers/frontend-component-library';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import SubscriptionService from '@/services/subscriptions/SubscriptionService';
import RestartAccountCouponField from '@/components/forms/fields/RestartAccountCouponField';
import SelectBox from '@/components/forms/register/SelectBox';
import RadioField from '@/components/forms/fields/RadioField.vue';
import { PRODUCT_FAMILY_LITE, PLAN_MONTHLY, PLAN_THREE_WEEKLY } from '@/constants/Gifting.js';
import FloatingBanner from '@/components/structure/FloatingBanner.vue';
import ChooseGiftType from '@/views/account/gifting/ChooseGiftType.vue';

export default {
    name: 'RestartAccount',
    data() {
        return {
            buttonDisabled: false,
            couponCode: null,
            selectedPlan: null,
            address: null,
            errorHandler: new ErrorResponseHandler(),
        };
    },
    computed: {
        ...mapGetters({
            isMonthlySubscriptionEnabled: 'account/isMonthlySubscriptionEnabled',
            firstName: 'account/getFirstName',
            inactiveSubscriptions: 'subscription/getInactiveSubscriptions',
            plans: 'subscription/getPlans',
            defaultProductFamily: 'register/getDefaultProductFamily',
            getProductTypes: 'subscription/getProductTypes',
            getAddressById: 'address/getAddressById',
            primaryProducts: 'register/getPrimaryProducts',
            restartFields: 'account/getrestartFields',
            isThreeWeeklyEnabled: 'account/isThreeWeeklyEnabled',
            outstandingOrders: 'account/getOutstandingOrders',
        }),
        inactiveId() {
            let arr = [];
            let result = this.inactiveSubscriptions.map(function(e) {
                return e.id;
            });

            for (let i = 0; i < result.length; i++) {
                arr.push(result[i]);
            };

            return arr;
        },
        availableProducts() {
            return this.primaryProducts.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        },
        selectedProduct: {
            get() {
                return this.$store.getters['register/getSelectedProductDetails'];
            },
            set(value) {
                this.$store.commit('register/setSelectedProductDetails', value);
            },
        },
        planOptions() {
            let filteredPlans = this.isThreeWeeklyEnabled && this.selectedProduct?.product_family !== PRODUCT_FAMILY_LITE ? this.plans : this.plans.filter(item => item.plan_slug !== PLAN_THREE_WEEKLY);
            if (this.selectedProduct?.product_family === PRODUCT_FAMILY_LITE) {
                return filteredPlans.filter(item => item.plan_slug !== PLAN_MONTHLY);
            }
            return filteredPlans;
        },
    },
    mounted() {
        this.$store.dispatch('subscription/fetchAllSubscriptions', true);
        this.$store.dispatch('register/getAllProducts');
        this.$store.dispatch('address/fetchAllAddresses', true);
        this.$store.dispatch('account/fetchOutstandingOrders');
    },
    methods: {
        restartSubscription(subscriptionId, couponCode, plan) {
            this.buttonDisabled = true;
            let service = new SubscriptionService();
            let selectedProductFromDelivery = this.getProductTypes.filter(item => item.product_family === this.selectedProduct.product_family);

            service.createSubscriptionFromLapsedSubscription({
                subscription_id: subscriptionId,
                coupon_code: couponCode,
                plan_slug: plan,
                primary_product_id: selectedProductFromDelivery[0]?.id,
            })
                .then(() => {
                    this.$store.dispatch('subscription/fetchAllSubscriptions', true);
                    setTimeout(() => {
                        this.$router.push({ name: 'account.restart-confirmation-page' });
                    }, 200);
                })
                .catch(error => {
                    setTimeout(() => this.errorHandler.setResponse(error.response && error.response.data), 0);
                    this.buttonDisabled = false;
                });
        },

        go(route) {
            this.$router.push({ name: route });
        },
    },
    watch: {
        inactiveSubscriptions: {
            deep: true,
            handler(newValue) {
                let deliveryAddressId = newValue[0]?.delivery_address_id;
                if (deliveryAddressId) {
                    this.address = this.getAddressById(deliveryAddressId);
                }
            },
        },
        address: {
            deep: true,
            handler(newAddress) {
                let postcode = newAddress.postcode;
                if (postcode && !this.getProductTypes.length) this.$store.dispatch('subscription/fetchAvailableDeliveryDay', postcode);
            },
        },
    },
    components: {
        RestartAccountCouponField,
        RadioField,
        MultiTeaser,
        SelectBox,
        FloatingBanner,
        ChooseGiftType,
    },
};
</script>

<style scoped lang="scss">
.full-page {
    margin-left: auto;
    margin-right: auto;
    text-align: left;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 4rem;
    width: 78vw;
}

    .restart-button {
        margin: 10px auto;
        width: 300px;
        height: 50px;
        border-radius: 4px;
        background-color: #262323;
    }
    .main-page {
        display: flex;
        max-width: 303px;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 0;
    }
    .discount-text {
        padding: 0px;
        width: 300px;
    }
    .sub-text {
        margin-bottom: 24px;
        margin-top: 24px;
        font-size: 20px;
        margin-left: 16px;
        margin-right: 16px;
        width: 130.5%;
    }
    .hero-image {
        padding-bottom: 24px;
    }
    .selectBox {
        width: 78vw;
    }

    .main-hero {
        margin-left: 16px;
        margin-right: 16px;
        width: 124%;
        text-align: center;
    }

    .tag-style {
        border: 0.5px solid #CECECE;
        width: 100%;
        height: 0px;
    }

    .button {
        &--greyed-out {
            background-color: lightgrey;
        }
    }

    @media (min-width: 740px) {
        .main-hero {
            text-align: left;
            width: 90%;
            margin-left: 0px;
            margin-right: 0px;
        }
         .selectBox {
            width: 612px;
            height: 470px;
        }
        .sub-text {
            width: 612px;
            margin-left: 0px;
            margin-right: 0px;
        }
         .main-page {
            max-width: 612px;
            margin: 0px auto;
        }
        .discount-text {
            width: 612px;
        }
        .full-page {
            width: 612px;
            text-align: left;
        }
    }

.restart {
    &--select-frequency {
        margin-top: 80px;
    }
}

@media (max-width: 768px) {
    .restart {
        &--select-frequency {
            margin-top: 8px;
        }
    }
}
</style>
